import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Building2, Users, Calendar, Settings, Shield, Plus, Trash2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';

const TABS = ['Profile', 'Team & Roles', 'Assignment Rules', 'Regulatory Calendar', 'Document Settings'] as const;
type Tab = typeof TABS[number];

const FACILITY_TYPES = ['critical_access_hospital', 'health_system', 'community_hospital', 'behavioral_health', 'fqhc', 'ambulatory_surgery', 'home_health', 'skilled_nursing', 'telehealth', 'multisite'];
const FRAMEWORKS = ['HIPAA', 'CMS', 'FDA', 'Joint Commission', 'SAMHSA', 'OIG', 'OSHA', 'State'];
const ACCREDITATION = ['joint_commission', 'dnv', 'hfap', 'achc', 'cihq', 'none', 'multiple'];
const PAYERS = ['medicare', 'medicaid', 'commercial', 'tricare', 'self_pay'];
const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

export default function OrganizationPage() {
  const [tab, setTab] = useState<Tab>('Profile');
  const qc = useQueryClient();

  const { data: profile, isLoading } = useQuery({
    queryKey: ['tenant-profile'],
    queryFn: () => api.tenant.profile(),
  });

  const { data: staffData } = useQuery({
    queryKey: ['tenant-staff'],
    queryFn: () => api.tenant.staff.list(),
    enabled: tab === 'Team & Roles',
  });

  const { data: calendarData } = useQuery({
    queryKey: ['tenant-calendar'],
    queryFn: () => api.tenant.calendar.list(),
    enabled: tab === 'Regulatory Calendar',
  });

  const { data: rulesData } = useQuery({
    queryKey: ['tenant-assignment-rules'],
    queryFn: () => api.tenant.assignmentRules.list(),
    enabled: tab === 'Assignment Rules',
  });

  // Profile completeness
  const completeness = profile ? calculateCompleteness(profile) : 0;

  const tabIcons: Record<Tab, React.ReactNode> = {
    'Profile': <Building2 className="w-4 h-4" />,
    'Team & Roles': <Users className="w-4 h-4" />,
    'Assignment Rules': <Shield className="w-4 h-4" />,
    'Regulatory Calendar': <Calendar className="w-4 h-4" />,
    'Document Settings': <Settings className="w-4 h-4" />,
  };

  if (isLoading) return <div className="p-6 text-gray-400">Loading...</div>;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Organization Settings</h1>
          <p className="text-sm text-gray-400 mt-1">Configure your compliance environment</p>
        </div>
        <div className="text-right">
          <div className="text-sm text-gray-400 mb-1">Profile Completeness</div>
          <div className="w-48 h-2 bg-gray-700 rounded-full overflow-hidden">
            <div className="h-full bg-teal-500 rounded-full transition-all" style={{ width: `${completeness}%` }} />
          </div>
          <div className="text-xs text-gray-500 mt-1">{completeness}%</div>
        </div>
      </div>

      <div className="flex gap-1 border-b border-gray-700">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === t ? 'border-teal-500 text-teal-400' : 'border-transparent text-gray-400 hover:text-white'
            }`}>
            {tabIcons[t]} {t}
          </button>
        ))}
      </div>

      {tab === 'Profile' && <ProfileTab profile={profile} qc={qc} />}
      {tab === 'Team & Roles' && <TeamTab staff={(staffData?.staff as readonly Record<string, unknown>[] | undefined) ?? []} qc={qc} />}
      {tab === 'Assignment Rules' && <RulesTab rules={(rulesData?.rules as readonly Record<string, unknown>[] | undefined) ?? []} qc={qc} />}
      {tab === 'Regulatory Calendar' && <CalendarTab events={(calendarData?.events as readonly Record<string, unknown>[] | undefined) ?? []} qc={qc} />}
      {tab === 'Document Settings' && <DocumentTab profile={profile} qc={qc} />}
    </div>
  );
}

function calculateCompleteness(p: Record<string, unknown>): number {
  const fields = ['facilityType', 'states', 'regulatoryFrameworks', 'accreditationBody', 'payerMix', 'serviceLines', 'ehrSystem', 'documentSystem', 'primaryState', 'orgSize'];
  let filled = 0;
  for (const f of fields) {
    const v = p[f];
    if (v && (!Array.isArray(v) || v.length > 0)) filled++;
  }
  return Math.round((filled / fields.length) * 100);
}

function ProfileTab({ profile, qc }: { readonly profile: Record<string, unknown> | undefined; readonly qc: ReturnType<typeof useQueryClient> }) {
  const [form, setForm] = useState<Record<string, unknown>>(profile ?? {});
  const update = (k: string, v: unknown) => setForm((p) => ({ ...p, [k]: v }));

  const mutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.tenant.updateProfile(data),
    onSuccess: () => { toast.success('Profile updated'); qc.invalidateQueries({ queryKey: ['tenant-profile'] }); },
    onError: () => toast.error('Failed to update profile'),
  });

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-gray-400 mb-1">Facility Type</label>
          <select value={(form.facilityType as string) ?? ''} onChange={(e) => update('facilityType', e.target.value)}
            className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white text-sm">
            <option value="">Select...</option>
            {FACILITY_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">Organization Size</label>
          <select value={(form.orgSize as string) ?? ''} onChange={(e) => update('orgSize', e.target.value)}
            className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white text-sm">
            <option value="">Select...</option>
            {['small', 'medium', 'large', 'enterprise'].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">Primary State</label>
          <select value={(form.primaryState as string) ?? ''} onChange={(e) => update('primaryState', e.target.value)}
            className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white text-sm">
            <option value="">Select...</option>
            {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">Bed Count</label>
          <input type="number" value={(form.bedCount as number) ?? ''} onChange={(e) => update('bedCount', parseInt(e.target.value) || null)}
            className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white text-sm" />
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">Accreditation Body</label>
          <select value={(form.accreditationBody as string) ?? ''} onChange={(e) => update('accreditationBody', e.target.value)}
            className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white text-sm">
            <option value="">Select...</option>
            {ACCREDITATION.map((a) => <option key={a} value={a}>{a.replace(/_/g, ' ')}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm text-gray-400 mb-1">EHR System</label>
          <input type="text" value={(form.ehrSystem as string) ?? ''} onChange={(e) => update('ehrSystem', e.target.value)}
            className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white text-sm" placeholder="e.g., Epic, Cerner" />
        </div>
      </div>

      <div>
        <label className="block text-sm text-gray-400 mb-2">Regulatory Frameworks</label>
        <div className="flex flex-wrap gap-2">
          {FRAMEWORKS.map((f) => {
            const selected = ((form.regulatoryFrameworks as string[]) ?? []).includes(f);
            return (
              <button key={f} onClick={() => {
                const cur = (form.regulatoryFrameworks as string[]) ?? [];
                update('regulatoryFrameworks', selected ? cur.filter((x) => x !== f) : [...cur, f]);
              }} className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${selected ? 'bg-teal-500/20 border-teal-500 text-teal-400' : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-500'}`}>
                {f}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <label className="block text-sm text-gray-400 mb-2">Payer Mix</label>
        <div className="flex flex-wrap gap-2">
          {PAYERS.map((p) => {
            const selected = ((form.payerMix as string[]) ?? []).includes(p);
            return (
              <button key={p} onClick={() => {
                const cur = (form.payerMix as string[]) ?? [];
                update('payerMix', selected ? cur.filter((x) => x !== p) : [...cur, p]);
              }} className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${selected ? 'bg-teal-500/20 border-teal-500 text-teal-400' : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-500'}`}>
                {p.replace(/_/g, ' ')}
              </button>
            );
          })}
        </div>
      </div>

      <button onClick={() => mutation.mutate(form)} disabled={mutation.isPending}
        className="flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-500 text-white rounded text-sm font-medium disabled:opacity-50">
        <Save className="w-4 h-4" /> Save Profile
      </button>
    </div>
  );
}

function TeamTab({ staff, qc }: { readonly staff: readonly Record<string, unknown>[]; readonly qc: ReturnType<typeof useQueryClient> }) {
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('compliance_rep');
  const [dept, setDept] = useState('');

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.tenant.staff.create(data),
    onSuccess: () => { toast.success('Staff member added'); qc.invalidateQueries({ queryKey: ['tenant-staff'] }); setShowForm(false); setName(''); setEmail(''); },
    onError: () => toast.error('Failed to add staff'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.tenant.staff.delete(id),
    onSuccess: () => { toast.success('Staff deactivated'); qc.invalidateQueries({ queryKey: ['tenant-staff'] }); },
  });

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-400">{staff.length} team members</p>
        <button onClick={() => setShowForm(!showForm)} className="flex items-center gap-2 px-3 py-1.5 bg-teal-600 hover:bg-teal-500 text-white rounded text-sm">
          <Plus className="w-4 h-4" /> Add Member
        </button>
      </div>

      {showForm && (
        <div className="bg-gray-800 rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" className="bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white text-sm" />
          <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className="bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white text-sm" />
          <select value={role} onChange={(e) => setRole(e.target.value)} className="bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white text-sm">
            <option value="compliance_rep">Compliance Rep</option>
            <option value="department_rep">Department Rep</option>
            <option value="cco">CCO</option>
            <option value="admin">Admin</option>
          </select>
          <input value={dept} onChange={(e) => setDept(e.target.value)} placeholder="Department" className="bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white text-sm" />
          <button onClick={() => createMutation.mutate({ fullName: name, email, role, department: dept })}
            className="col-span-full px-4 py-2 bg-teal-600 hover:bg-teal-500 text-white rounded text-sm">Save</button>
        </div>
      )}

      <div className="space-y-2">
        {staff.map((s) => (
          <div key={s.staffId as string} className="flex items-center justify-between bg-gray-800/50 rounded-lg px-4 py-3">
            <div>
              <div className="text-white font-medium text-sm">{s.fullName as string}</div>
              <div className="text-xs text-gray-400">{s.email as string} &middot; {(s.role as string).replace(/_/g, ' ')} &middot; {(s.department as string) || 'No dept'}</div>
            </div>
            <button onClick={() => deleteMutation.mutate(s.staffId as string)} className="text-gray-500 hover:text-red-400" aria-label="Remove staff member">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function RulesTab({ rules }: { readonly rules: readonly Record<string, unknown>[]; readonly qc: ReturnType<typeof useQueryClient> }) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-400">{rules.length} assignment rules configured</p>
      {rules.map((r) => (
        <div key={r.ruleId as string} className="bg-gray-800/50 rounded-lg px-4 py-3">
          <div className="text-white font-medium text-sm">{r.name as string}</div>
          <div className="text-xs text-gray-400 mt-1">
            Frameworks: {((r.frameworks as string[]) ?? []).join(', ') || 'Any'} &middot;
            Assign to: {(r.assignToRole as string) || (r.assignToDepartment as string) || 'Unset'}
          </div>
        </div>
      ))}
      {rules.length === 0 && <p className="text-sm text-gray-500 text-center py-8">No assignment rules configured yet</p>}
    </div>
  );
}

function CalendarTab({ events, qc }: { readonly events: readonly Record<string, unknown>[]; readonly qc: ReturnType<typeof useQueryClient> }) {
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [type, setType] = useState('deadline');

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.tenant.calendar.create(data),
    onSuccess: () => { toast.success('Event added'); qc.invalidateQueries({ queryKey: ['tenant-calendar'] }); setShowForm(false); setTitle(''); },
  });

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-400">{events.length} upcoming events</p>
        <button onClick={() => setShowForm(!showForm)} className="flex items-center gap-2 px-3 py-1.5 bg-teal-600 hover:bg-teal-500 text-white rounded text-sm">
          <Plus className="w-4 h-4" /> Add Event
        </button>
      </div>

      {showForm && (
        <div className="bg-gray-800 rounded-lg p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Event title" className="bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white text-sm" />
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white text-sm" />
          <select value={type} onChange={(e) => setType(e.target.value)} className="bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white text-sm">
            {['survey', 'accreditation_renewal', 'deadline', 'comment_period', 'effective_date', 'internal_audit', 'board_review'].map((t) =>
              <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
            )}
          </select>
          <button onClick={() => createMutation.mutate({ title, eventDate: date, eventType: type })}
            className="col-span-full px-4 py-2 bg-teal-600 hover:bg-teal-500 text-white rounded text-sm">Save</button>
        </div>
      )}

      <div className="space-y-2">
        {events.map((e) => (
          <div key={e.eventId as string} className="flex items-center justify-between bg-gray-800/50 rounded-lg px-4 py-3">
            <div>
              <div className="text-white font-medium text-sm">{e.title as string}</div>
              <div className="text-xs text-gray-400">{new Date(e.eventDate as string).toLocaleDateString()} &middot; {(e.eventType as string).replace(/_/g, ' ')}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DocumentTab({ profile, qc }: { readonly profile: Record<string, unknown> | undefined; readonly qc: ReturnType<typeof useQueryClient> }) {
  const [namingConvention, setNamingConvention] = useState((profile?.namingConvention as string) ?? '');
  const [versionFormat, setVersionFormat] = useState((profile?.versionFormat as string) ?? '');
  const [docSystem, setDocSystem] = useState((profile?.documentSystem as string) ?? '');

  const mutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.tenant.updateProfile(data),
    onSuccess: () => { toast.success('Document settings saved'); qc.invalidateQueries({ queryKey: ['tenant-profile'] }); },
  });

  return (
    <div className="space-y-4 max-w-xl">
      <div>
        <label className="block text-sm text-gray-400 mb-1">Document Management System</label>
        <select value={docSystem} onChange={(e) => setDocSystem(e.target.value)}
          className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white text-sm">
          <option value="">Select...</option>
          {['sharepoint', 'google_drive', 'sftp', 'network_drive', 'policy_stat', 'powerdms', 'other'].map((s) =>
            <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
          )}
        </select>
      </div>
      <div>
        <label className="block text-sm text-gray-400 mb-1">Naming Convention</label>
        <input value={namingConvention} onChange={(e) => setNamingConvention(e.target.value)} placeholder="e.g., [DEPT]-[TYPE]-[NUM]-v[VER]"
          className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white text-sm" />
      </div>
      <div>
        <label className="block text-sm text-gray-400 mb-1">Version Format</label>
        <input value={versionFormat} onChange={(e) => setVersionFormat(e.target.value)} placeholder="e.g., v1.0, 1.0.0"
          className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-white text-sm" />
      </div>
      <button onClick={() => mutation.mutate({ documentSystem: docSystem, namingConvention, versionFormat })}
        className="flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-500 text-white rounded text-sm">
        <Save className="w-4 h-4" /> Save Settings
      </button>
    </div>
  );
}
